'use client'

import { type ChangeEvent, useMemo } from 'react'
import { Button } from '@acko/button'
import { Checkbox } from '@acko/checkbox'
import { TextInput } from '@acko/text-input'
import { Typography } from '@acko/typography'
import { formatAadhaarDisplay } from '@helpers/abhaWidgetHelper'
import { ABHA_HERO_MODAL_AADHAAR_STEP_DATA, ABHA_HERO_PHONE_MAX_LENGTH } from '@components/NewProduct/constants'
import { ModalDisclaimer } from '@components/NewProduct/AbhaHeroWidget/ModalDisclaimer'
import { resolveAbhaHeroStepData } from '@components/NewProduct/AbhaHeroWidget/steps/abhaHeroStepData'

export type AbhaHeroAadhaarStepContent = {
  title: string
  description: string
  inputLabel: string
  inputPlaceholder: string
  inputHelper: string
  phoneInputLabel: string
  phoneInputPlaceholder: string
  consentText: string
  disclaimerText: string
  disclaimerIconSrc: string
  ctaText: string
}

export type AbhaHeroAadhaarStepProps = {
  data?: Record<string, unknown>
  isMobile: boolean
  aadhaar: string
  aadhaarError: string
  showPhoneInput?: boolean
  phone?: string
  phoneError?: string
  hasConsent: boolean
  submitError: string
  isSendOtpEnabled: boolean
  isSubmitting: boolean
  onAadhaarChange: (event: ChangeEvent<HTMLInputElement>) => void
  onAadhaarBlur: () => void
  onPhoneChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onPhoneBlur?: () => void
  onConsentChange: (checked: boolean) => void
  onSendOtp: () => void
}

const AADHAAR_STEP_DEFAULTS: AbhaHeroAadhaarStepContent = {
  title: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.TITLE,
  description: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.DESCRIPTION,
  inputLabel: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.LABEL,
  inputPlaceholder: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.PLACEHOLDER,
  inputHelper: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.HELPER,
  phoneInputLabel: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.PHONE_LABEL,
  phoneInputPlaceholder: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.PHONE_PLACEHOLDER,
  consentText: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.CONSENT_TEXT,
  disclaimerText: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.DISCLAIMER_TEXT,
  disclaimerIconSrc: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.DISCLAIMER_ICON_SRC,
  ctaText: ABHA_HERO_MODAL_AADHAAR_STEP_DATA.CTA_TEXT,
}

export const AbhaHeroAadhaarStep = ({
  data,
  isMobile,
  aadhaar,
  aadhaarError,
  showPhoneInput = false,
  phone = '',
  phoneError = '',
  hasConsent,
  submitError,
  isSendOtpEnabled,
  isSubmitting,
  onAadhaarChange,
  onAadhaarBlur,
  onPhoneChange,
  onPhoneBlur,
  onConsentChange,
  onSendOtp,
}: AbhaHeroAadhaarStepProps) => {
  const stepData = useMemo(() => resolveAbhaHeroStepData(data, isMobile, AADHAAR_STEP_DEFAULTS), [data, isMobile])

  return (
    <div className="abha-hero-modal__content abha-hero-modal__content--form">
      <div className="abha-hero-modal__scroll">
        <div className="abha-hero-modal__intro">
          <Typography variant="heading-lg" as="h2" color="primary" className="abha-hero-modal__intro-title">
            {stepData.title}
          </Typography>
          <Typography scale="sm" emphasis="normal" color="secondary" className="abha-hero-modal__intro-description">
            {stepData.description}
          </Typography>
        </div>

        <div className="abha-hero-modal__aadhaar-consent">
          <TextInput
            className="abha-hero-modal__aadhaar-input"
            label={stepData.inputLabel}
            inputType="aadhaar"
            size="lg"
            placeholder={stepData.inputPlaceholder}
            helperText={stepData.inputHelper}
            value={formatAadhaarDisplay(aadhaar)}
            maxLength={14}
            error={Boolean(aadhaarError)}
            errorText={aadhaarError || undefined}
            onChange={onAadhaarChange}
            onBlur={onAadhaarBlur}
            autoComplete="off"
          />

          <div className="abha-hero-modal__consent">
            <Checkbox
              className="abha-hero-modal__consent-checkbox"
              checked={hasConsent}
              onChange={onConsentChange}
              size="sm"
            />
            <div className="abha-hero-modal__consent-scroll">
              <Typography scale="xs" emphasis="normal" color="secondary" className="abha-hero-modal__consent-text">
                {stepData.consentText}
              </Typography>
            </div>
          </div>
        </div>

        {showPhoneInput && (
          <TextInput
            className="abha-hero-modal__phone-input"
            label={stepData.phoneInputLabel}
            inputType="phone"
            size="lg"
            placeholder={stepData.phoneInputPlaceholder}
            value={phone}
            maxLength={ABHA_HERO_PHONE_MAX_LENGTH}
            error={Boolean(phoneError)}
            errorText={phoneError || undefined}
            onChange={onPhoneChange}
            onBlur={onPhoneBlur}
            autoComplete="tel"
          />
        )}

        {submitError && (
          <Typography scale="xs" emphasis="normal" color="error">
            {submitError}
          </Typography>
        )}
      </div>

      <div className="abha-hero-modal__footer">
        <div className="abha-hero-modal__footer-disclaimer">
          <ModalDisclaimer text={stepData.disclaimerText} iconSrc={stepData.disclaimerIconSrc} />
        </div>

        <div className="abha-hero-modal__footer-action">
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            disabled={!isSendOtpEnabled}
            loading={isSubmitting}
            onClick={onSendOtp}>
            {stepData.ctaText}
          </Button>
        </div>
      </div>
    </div>
  )
}
